import {
  buildWebSocketUrl,
  connectReconnectingSocket,
  type LiveConnectionState,
  type ReconnectingSocketHandle,
} from './reconnectingSocket';

export interface SmsDeliveryUpdatedMessage {
  type: 'sms.deliveryUpdated';
  reference: string;
  status: string;
  cmStatusCode: number | null;
  errorCode: string | null;
  errorDescription: string | null;
  destinationCiphertextBase64: string;
  updatedAt: string;
  failedAt: string | null;
}

type SmsDeliveryLiveMessage = SmsDeliveryUpdatedMessage | { type: 'sms.connected' };

export interface SmsDeliveryLiveOptions {
  onUpdate: (message: SmsDeliveryUpdatedMessage) => void;
  onStateChange?: (state: LiveConnectionState) => void;
}

let activeSocket: ReconnectingSocketHandle | null = null;
/** When true, soft disconnects (e.g. InformationPage sync) must not tear down the socket. */
let holdOpen = false;

export function holdSmsDeliveryLive(hold: boolean): void {
  holdOpen = hold;
}

export function isSmsDeliveryLiveHeld(): boolean {
  return holdOpen;
}

export function connectSmsDeliveryLive(options: SmsDeliveryLiveOptions): ReconnectingSocketHandle {
  // Replace any existing socket (even while held — caller is taking ownership).
  activeSocket?.close();
  activeSocket = connectReconnectingSocket({
    url: buildWebSocketUrl('/api/ws/sms-delivery'),
    onStateChange: options.onStateChange,
    onMessage: (payload) => {
      const message = payload as SmsDeliveryLiveMessage;
      if (message.type === 'sms.deliveryUpdated') {
        options.onUpdate(message);
      }
    },
  });
  return activeSocket;
}

export function disconnectSmsDeliveryLive(options?: { force?: boolean }): void {
  if (holdOpen && !options?.force) {
    return;
  }

  activeSocket?.close();
  activeSocket = null;
}

export function isSmsDeliveryLiveActive(): boolean {
  return activeSocket !== null;
}

export function isSmsDeliveryLiveConnected(): boolean {
  return activeSocket?.getState() === 'connected';
}

/** True when a live update can show/hide the info-share failure banner (resync socket). */
export function smsDeliveryUpdateAffectsFailureBanner(status: string): boolean {
  return status === 'failed' || status === 'delivered';
}
